/**
 * @file Patient Detail Page — /paciente/:id
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarX } from "lucide-react";
import { usePatientDetail } from "../hooks/usePatients";
import { useAppointments } from "../hooks/useAppointments";
import { PATIENT_STRINGS, APPOINTMENT_STRINGS, COMMON_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  CardHeader,
  Chip,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "../ui";
import "./PatientDetail.css";

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

export default function PatientDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { patient, isLoading } = usePatientDetail(parseInt(id));
  const { appointments, isLoading: loadingAppts } = useAppointments();

  const patientAppointments = appointments.filter(
    (a) => a.patientId === parseInt(id)
  );

  if (isLoading) {
    return (
      <div className="pd-page">
        <div className="sk-block sk-h-200" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="pd-page">
        <p>{COMMON_STRINGS.NOT_FOUND_PATIENT}</p>
      </div>
    );
  }

  return (
    <div className="pd-page">
      <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
        {COMMON_STRINGS.BACK}
      </Button>

      <Card>
        <h1 className="pd-name">
          {patient.firstName} {patient.lastName}
        </h1>
        <div className="pd-grid">
          <div className="pd-cell">
            <span className="pd-label">Correo</span>
            <span className="pd-value">
              {patient.email ? (
                <a href={`mailto:${patient.email}`} className="pd-link">
                  {patient.email}
                </a>
              ) : (
                "—"
              )}
            </span>
          </div>
          <div className="pd-cell">
            <span className="pd-label">Teléfono</span>
            <span className="pd-value">
              {patient.phone ? (
                <a href={`tel:${patient.phone}`} className="pd-link">
                  {patient.phone}
                </a>
              ) : (
                "—"
              )}
            </span>
          </div>
          <div className="pd-cell">
            <span className="pd-label">{PATIENT_STRINGS.DATE_OF_BIRTH}</span>
            <span className="pd-value">
              {patient.dateOfBirth
                ? new Date(patient.dateOfBirth).toLocaleDateString("es-MX")
                : "—"}
            </span>
          </div>
        </div>
      </Card>

      <h2 className="pd-section-title">{PATIENT_STRINGS.APPOINTMENT_HISTORY}</h2>

      {loadingAppts ? (
        <div className="pd-loading">
          <Spinner size={28} />
        </div>
      ) : patientAppointments.length === 0 ? (
        <Card className="pd-empty">
          <CalendarX size={36} className="pd-empty-icon" />
          <p>{PATIENT_STRINGS.NO_APPOINTMENTS}</p>
        </Card>
      ) : (
        <Card padded={false} className="pd-table-wrap">
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Estado</TH>
                <TH>Motivo</TH>
              </TR>
            </THead>
            <TBody>
              {patientAppointments.map((appt) => (
                <TR
                  key={appt.id}
                  onClick={() => navigate(`/cita/${appt.id}`)}
                  className="pd-row"
                >
                  <TD>
                    <span className="pd-date">
                      {new Date(appt.dateTime).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </TD>
                  <TD>
                    <Chip tone={STATUS_TONES[appt.status] || "neutral"} size="sm">
                      {APPOINTMENT_STRINGS.STATUS_LABELS[appt.status]}
                    </Chip>
                  </TD>
                  <TD>{appt.reason || "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
